import { Drawer, Form } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { useBulkEditSave } from 'app/hooks/data/useBulkEditSave';
import type {
  BulkEditFormValues,
  BulkEditInitialValues,
  IBulkEditPayload,
} from 'app/typings/bulkEdit';
import { STANDARD_DATE_FORMAT } from 'app/utils/dates';
import type { Interval } from 'app/utils/dayjs';
import { eachDayOfInterval, format } from 'app/utils/dayjs';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { ValidationBulkEditModal } from '../ValidationBulkEditModal/ValidationBulkEditModal';

import './BulkEdit.scss';
import { BulkEditFooter } from './_components/BulkEditFooter';
import { BulkEditValues } from './_components/BulkEditValues';
import {
  BULK_EDIT_DEFAULT_DAYS,
  DAYS_OPTIONS,
  DatesSelector,
} from './_components/DatesSelector';
import { RoomSelector } from './_components/RoomSelector';

export const RULES = [{ required: true, message: 'Required' }];

export type FieldsDisplayValues = RoomDisplayValues & {
  packageDisplayValues: PackageDisplayValues[];
};

export type RoomDisplayValues = { stock: boolean; bar: boolean };

const isRoomValueKey = (key: string): key is keyof RoomDisplayValues => {
  return ['stock', 'bar'].includes(key);
};

export type PackageDisplayValues = {
  packageId: number;
  discountPrice: boolean;
  discountRate: boolean;
};

const isPackageValueKey = (key: string): key is keyof PackageDisplayValues => {
  return ['discountPrice', 'discountRate'].includes(key);
};

type Props = {
  isBulkEditOpen: boolean;
  setIsBulkEditOpen: (isBulkEditOpen: boolean) => void;
  initialValues: BulkEditInitialValues;
  currentRange?: Interval;
  hotelId: number;
  autoDiscountRate?: number;
};

export const BulkEdit = ({
  setIsBulkEditOpen,
  isBulkEditOpen,
  initialValues,
  currentRange,
  hotelId,
  autoDiscountRate,
}: Props) => {
  const [form] = Form.useForm();

  const { getPackagesForRoom, packages } = useContext(LegacyHotelContext);

  const roomId = form.getFieldValue('roomId');

  const roomPackages = useMemo(
    () => getPackagesForRoom(roomId),
    [getPackagesForRoom, roomId]
  );

  const packageValuesInitialSwitchValues = useMemo(
    () =>
      packages.map((pkg) => ({
        packageId: pkg.id,
        discountPrice: false,
        discountRate: false,
      })),
    [packages]
  );

  const initialDisplayValues = {
    stock: false,
    bar: false,
    packageDisplayValues: packageValuesInitialSwitchValues,
  };

  const formValues = Form.useWatch<BulkEditFormValues>([], form);

  const { data, isLoading, mutate } = useBulkEditSave(hotelId);

  useEffect(() => form.setFieldsValue(initialValues), [form, initialValues]);

  const [isValidationBulkEditModalOpen, setIsValidationBulkEditModalOpen] =
    useState<boolean>(false);

  const [payload, setPayload] = useState<IBulkEditPayload>({
    hotelId: 0,
    roomId: initialValues.roomId ?? 0,
    startDate: initialValues.startDate ?? new Date(),
    endDate: initialValues.endDate ?? new Date(),
    days: initialValues.days ?? BULK_EDIT_DEFAULT_DAYS,
    stock: initialValues.stock,
    bar: initialValues.bar,
    packagesRates: roomPackages.map((pkg) => ({
      packageId: pkg.id,
      ...initialValues[pkg.id],
    })),
    dryRun: true,
  });

  const [openValues, setOpenValues] =
    useState<FieldsDisplayValues>(initialDisplayValues);

  const onFinish = (values: BulkEditFormValues) => {
    const packagesRates = roomPackages.reduce(
      (acc, pkg) => {
        return values[pkg.id].discountPrice !== undefined ||
          values[pkg.id].discountRate !== undefined
          ? [
              ...acc,
              {
                packageId: pkg.id,
                ...values[pkg.id],
              },
            ]
          : acc;
      },
      [] as {
        packageId: number;
        discountPrice?: number | undefined;
        discountRate?: number | undefined;
      }[]
    );

    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const payload: IBulkEditPayload = {
      days: values.days,
      bar: values.bar,
      stock: values.stock,
      roomId: values.roomId,
      packagesRates,
      hotelId,
      startDate: format(values.startDate, STANDARD_DATE_FORMAT),
      endDate: format(values.endDate, STANDARD_DATE_FORMAT),
      dryRun: true,
    };

    setPayload(payload);
    mutate(payload);

    const amplitudeValue = {
      from: format(values.startDate, STANDARD_DATE_FORMAT),
      to: format(values.endDate, STANDARD_DATE_FORMAT),
      daysChanged: DAYS_OPTIONS.filter((day) =>
        values.days.some((daySelect: number) => day.value === daySelect)
      ).map((day) => day.fullName),
      numberDaysChanged: eachDayOfInterval({
        start: values.startDate,
        end: values.endDate,
      }).filter((x) =>
        values.days.some(
          (daySelect: number) =>
            new Date(x).getDay() === (daySelect === 7 ? 0 : daySelect)
        )
      ).length,
      stock: values.stock || null,
      bar: values.bar || null,
      packagesRates,
    };

    amplitudeTrack('Save Bulk Edit', amplitudeValue);
    setIsValidationBulkEditModalOpen(true);
  };

  const toggleHotelValueSwitch = (switchValue: boolean, section: string) => {
    setOpenValues((values) => ({ ...values, [section]: switchValue }));

    !switchValue && form.setFieldValue([section], undefined);
  };

  const togglePackageValueSwitch = (
    switchValue: boolean,
    section: keyof PackageDisplayValues,
    packageId: number
  ) => {
    const packageValue = openValues.packageDisplayValues.find(
      (value) => value.packageId === packageId
    );

    if (!packageValue) {
      return;
    }

    const newOpenValue = { ...packageValue, [section]: switchValue };

    const newOpenValues = {
      ...openValues,
      packageDisplayValues: [
        ...openValues.packageDisplayValues.filter(
          (value) => value.packageId !== packageId
        ),
        newOpenValue,
      ],
    };

    setOpenValues(newOpenValues);

    !switchValue && form.setFieldValue([packageId, section], undefined);
  };

  const toggleSwitch = (
    switchValue: boolean,
    section: keyof PackageDisplayValues | keyof RoomDisplayValues,
    packageId?: number
  ) => {
    if (!!packageId && isPackageValueKey(section)) {
      togglePackageValueSwitch(switchValue, section, packageId);
    } else if (isRoomValueKey(section)) {
      toggleHotelValueSwitch(switchValue, section);
    }

    roomPackages
      .map((pkg) => [pkg.id, 'discountPrice'])
      .forEach((field) => form.validateFields(field));
    roomPackages
      .map((pkg) => [pkg.id, 'discountRate'])
      .forEach((field) => form.validateFields(field));
  };

  const resetPackageInitialValues = useCallback(() => {
    setOpenValues({
      bar: openValues.bar,
      stock: openValues.stock,
      packageDisplayValues: initialDisplayValues.packageDisplayValues,
    });

    roomPackages
      .map((pkg) => [pkg.id, 'discountPrice'])
      .forEach((field) => {
        form.resetFields(field);
        form.validateFields(field);
      });
    roomPackages
      .map((pkg) => [pkg.id, 'discountRate'])
      .forEach((field) => {
        form.resetFields(field);
        form.validateFields(field);
      });
  }, [
    form,
    initialDisplayValues.packageDisplayValues,
    openValues.bar,
    openValues.stock,
    roomPackages,
  ]);

  const handleClose = () => {
    setIsBulkEditOpen(false);
    form.resetFields();
    setOpenValues(initialDisplayValues);
  };

  const onRoomChange = () => {
    resetPackageInitialValues();
  };

  return (
    <Drawer
      title={
        <div className="bulkEdit__header">
          <h1>Bulk Edit</h1>
        </div>
      }
      className="bulkEdit"
      width="510px"
      closable={false}
      open={isBulkEditOpen}
      bodyStyle={{ padding: 0 }}
      onClose={handleClose}
      destroyOnClose
    >
      <Form
        name="bulkEditForm"
        autoComplete="off"
        className="form"
        form={form}
        onFinish={onFinish}
      >
        <div className="bulkEdit__body">
          <RoomSelector
            initialRoomId={initialValues.roomId}
            onChange={onRoomChange}
          />
          <DatesSelector
            fromDate={formValues?.startDate}
            days={initialValues.days}
            startDate={initialValues.startDate}
            endDate={initialValues.endDate}
          />
          <BulkEditValues
            initialValues={initialValues}
            formValues={formValues}
            openValues={openValues}
            toggleSwitch={toggleSwitch}
            resetInitialValues={resetPackageInitialValues}
            packages={roomPackages}
          />
        </div>
        <BulkEditFooter
          handleClose={handleClose}
          form={form}
          isLoading={isLoading}
          openValues={openValues}
        />
      </Form>
      {isValidationBulkEditModalOpen && !isLoading && data && currentRange && (
        <ValidationBulkEditModal
          setIsValidationBulkEditModalOpen={setIsValidationBulkEditModalOpen}
          hotelId={hotelId}
          currentRange={currentRange}
          payload={payload}
          dryRunResults={data}
          autoDiscountRate={autoDiscountRate}
          handleClose={handleClose}
          nbPackages={roomPackages.length}
        />
      )}
    </Drawer>
  );
};
